/**
 * System domain event schemas.
 *
 * Typed payload validation for all system-related Mycelium events.
 * Rule: fields added after initial release MUST be `.optional()`.
 *
 * @module system.events
 */

import { z } from 'zod'
import { BackboneEventSchema } from '../types/backbone.ts'

// ── Event schemas ───────────────────────────────────────────────────────

export const ServiceHealthCheckEvent = BackboneEventSchema.extend({
  domain: z.literal('system'),
  type: z.literal('service_health_check'),
  payload: z.object({
    serviceName: z.string(),
    status: z.enum(['healthy', 'degraded', 'unhealthy']),
    serviceType: z.string().optional(),
    domains: z.array(z.string()).optional(),
    details: z.record(z.unknown()).optional(),
    checkedAt: z.string(),
    source: z.string(),
    priority: z.string().optional(),
  }),
})

export const TokenBudgetUsedEvent = BackboneEventSchema.extend({
  domain: z.literal('system.budget'),
  type: z.literal('token_budget_used'),
  payload: z.object({
    agentName: z.string(),
    runId: z.string(),
    model: z.string().optional(),
    estimatedPromptTokens: z.number().int(),
    estimatedCompletionTokens: z.number().int(),
    estimatedTotalTokens: z.number().int(),
    source: z.string(),
    specialist: z.string().optional(),
  }),
})

export const SystemWarningReportedEvent = BackboneEventSchema.extend({
  domain: z.literal('system'),
  type: z.literal('system_warning_reported'),
  payload: z.object({
    source: z.string(),
    level: z.literal('warn'),
    message: z.string(),
    namespace: z.string().optional(),
    reportedAt: z.string(),
    priority: z.string().optional(),
    context: z.record(z.unknown()).optional(),
    tags: z.array(z.string()).optional(),
  }),
})

export const SystemErrorReportedEvent = BackboneEventSchema.extend({
  domain: z.literal('system'),
  type: z.literal('system_error_reported'),
  payload: z.object({
    source: z.string(),
    level: z.enum(['error', 'critical']),
    message: z.string(),
    namespace: z.string().optional(),
    reportedAt: z.string(),
    priority: z.string().optional(),
    context: z.record(z.unknown()).optional(),
    tags: z.array(z.string()).optional(),
  }),
})

// ── All system events ───────────────────────────────────────────────────

export const SystemDomainEvents = [
  ServiceHealthCheckEvent,
  TokenBudgetUsedEvent,
  SystemWarningReportedEvent,
  SystemErrorReportedEvent,
] as const
